import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePayeesProjection, PayeesProjectionMonth } from './useData';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ReportExportButtons } from '../../components/reports/ReportExportButtons';
import { exportReportToExcel, exportReportToPDF } from '../../utils/reportExport';
import { formatCurrency } from '../../utils/formatCurrency';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const { data: months, isLoading, isError } = usePayeesProjection(selectedYear);

  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i).sort((a, b) => a - b);

  const totalProjected = months.reduce((sum, m) => sum + m.totalProjected, 0);
  const totalActual = months.reduce((sum, m) => sum + m.actualExpenses, 0);
  const totalPending = months.reduce((sum, m) => sum + m.pendingPayables, 0);
  const totalPaid = months.reduce((sum, m) => sum + m.paidPayables, 0);
  const totalRecurring = months.reduce((sum, m) => sum + m.recurringProjected, 0);

  const reportTitle = `Payees Projection · ${selectedYear}`;
  const fileName = `payees-projection-${selectedYear}`;
  const onExportExcel = useCallback(async () => {
    const headers = ['Month', 'Actual expenses', 'Pending payables', 'Paid payables', 'Recurring projected', 'Total projected'];
    const rows = months.map((m) => [
      MONTH_NAMES[m.month - 1],
      formatCurrency(m.actualExpenses),
      formatCurrency(m.pendingPayables),
      formatCurrency(m.paidPayables),
      formatCurrency(m.recurringProjected),
      formatCurrency(m.totalProjected),
    ]);
    const totalRow = [
      'Total',
      formatCurrency(totalActual),
      formatCurrency(totalPending),
      formatCurrency(totalPaid),
      formatCurrency(totalRecurring),
      formatCurrency(totalProjected),
    ];
    await exportReportToExcel({
      reportTitle,
      fileName,
      table: { headers, rows: [...rows, totalRow] },
      rightAlignColumnIndices: [1, 2, 3, 4, 5],
    });
  }, [months, selectedYear, totalActual, totalPending, totalPaid, totalRecurring, totalProjected]);
  const onExportPDF = useCallback(async () => {
    const headers = ['Month', 'Actual expenses', 'Pending payables', 'Paid payables', 'Recurring projected', 'Total projected'];
    const rows = months.map((m) => [
      MONTH_NAMES[m.month - 1],
      formatCurrency(m.actualExpenses),
      formatCurrency(m.pendingPayables),
      formatCurrency(m.paidPayables),
      formatCurrency(m.recurringProjected),
      formatCurrency(m.totalProjected),
    ]);
    const totalRow = [
      'Total',
      formatCurrency(totalActual),
      formatCurrency(totalPending),
      formatCurrency(totalPaid),
      formatCurrency(totalRecurring),
      formatCurrency(totalProjected),
    ];
    await exportReportToPDF({
      reportTitle,
      fileName,
      table: { headers, rows: [...rows, totalRow] },
      rightAlignColumnIndices: [1, 2, 3, 4, 5],
    });
  }, [months, selectedYear, totalActual, totalPending, totalPaid, totalRecurring, totalProjected]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" message="Loading report..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className={cn('border-l-4 rounded-lg p-4 shadow-sm', colors.error)}>
          <div className="flex items-center">
            <svg className={cn('w-5 h-5 mr-3', colors.text)} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className={cn('font-medium', colors.text)}>Error loading report. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('h-full flex flex-col min-h-0', colors.background)}>
      <div className="flex-shrink-0 p-3 pb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className={cn('text-xs font-medium mr-1', colors.textMuted)}>Year</span>
          <div className={cn('inline-flex rounded-lg p-1 gap-1.5 border', colors.border, colors.surface)} role="group" aria-label="Select year">
            {yearOptions.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setSelectedYear(y)}
                className={cn(
                  'min-w-[2.75rem] px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors',
                  selectedYear === y
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : cn(colors.text, colors.surfaceHover, 'hover:bg-gray-100 dark:hover:bg-gray-700')
                )}
                aria-pressed={selectedYear === y}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
        <ReportExportButtons onExportExcel={onExportExcel} onExportPDF={onExportPDF} disabled={!months || months.length === 0} />
      </div>
      <p className={cn('flex-shrink-0 px-3 pb-2 text-xs', colors.textMuted)}>
        Add bills in <Link to="/payables" className="text-indigo-600 dark:text-indigo-400 hover:underline">Payables</Link> and mark as paid when done. Manage who you pay in <Link to="/payees" className="text-indigo-600 dark:text-indigo-400 hover:underline">Payees</Link>.
      </p>

      {months && months.length > 0 && (
        <div className={cn('flex-shrink-0 mx-3 mb-3 px-4 py-3 rounded-xl border flex flex-wrap items-center justify-between gap-4', colors.surface, colors.border, theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-50/80')}>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-right">
              <p className={cn('text-[10px] uppercase tracking-wider', colors.textMuted)}>Actual</p>
              <p className={cn('text-sm font-semibold', colors.text)}>{formatCurrency(totalActual)}</p>
            </div>
            <div className="w-px h-8 bg-gray-300 dark:bg-gray-600" />
            <div className="text-right">
              <p className={cn('text-[10px] uppercase tracking-wider', colors.textMuted)}>Pending</p>
              <p className={cn('text-sm font-semibold', colors.text)}>{formatCurrency(totalPending)}</p>
            </div>
            <div className="w-px h-8 bg-gray-300 dark:bg-gray-600" />
            <div className="text-right">
              <p className={cn('text-[10px] uppercase tracking-wider', colors.textMuted)}>Paid</p>
              <p className={cn('text-sm font-semibold', colors.text)}>{formatCurrency(totalPaid)}</p>
            </div>
            <div className="w-px h-8 bg-gray-300 dark:bg-gray-600" />
            <div className="text-right">
              <p className={cn('text-[10px] uppercase tracking-wider', colors.textMuted)}>Recurring</p>
              <p className={cn('text-sm font-semibold', colors.text)}>{formatCurrency(totalRecurring)}</p>
            </div>
            <div className="w-px h-8 bg-gray-300 dark:bg-gray-600" />
            <div className="text-right">
              <p className={cn('text-[10px] uppercase tracking-wider', colors.textMuted)}>Total projected</p>
              <p className={cn('text-sm font-bold text-indigo-600 dark:text-indigo-400', colors.text)}>{formatCurrency(totalProjected)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto px-3 pb-3">
        {months && months.length > 0 ? (
          <div className={cn('rounded-xl border overflow-hidden shadow-md h-full flex flex-col min-h-0', colors.surface, colors.border, theme === 'dark' ? 'from-gray-800 to-gray-800' : 'from-white to-gray-50', 'bg-gradient-to-br')}>
            <div className="flex-1 min-h-0 overflow-auto">
              <table className={cn('min-w-full divide-y', colors.divide)}>
                <caption className="sr-only">Payees projection by month for {selectedYear}</caption>
                <thead className={cn('sticky top-0 z-10 border-b', colors.surface, colors.border)}>
                  <tr>
                    <th className={cn('px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
                      Month
                    </th>
                    <th className={cn('px-4 py-4 text-right text-xs font-semibold uppercase tracking-wider min-w-[100px]', colors.textMuted)}>
                      Actual
                    </th>
                    <th className={cn('px-4 py-4 text-right text-xs font-semibold uppercase tracking-wider min-w-[100px]', colors.textMuted)}>
                      Pending
                    </th>
                    <th className={cn('px-4 py-4 text-right text-xs font-semibold uppercase tracking-wider min-w-[100px]', colors.textMuted)}>
                      Paid
                    </th>
                    <th className={cn('px-4 py-4 text-right text-xs font-semibold uppercase tracking-wider min-w-[100px]', colors.textMuted)}>
                      Recurring
                    </th>
                    <th className={cn('px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider sticky right-0 z-10 min-w-[120px]', colors.textMuted, colors.surface)}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className={cn('divide-y', colors.surface, colors.divide)}>
                  {months.map((row: PayeesProjectionMonth) => (
                    <tr key={`${row.year}-${row.month}`} className={cn('transition-colors border-b', colors.border, 'hover:bg-gray-50 dark:hover:bg-gray-700/50')}>
                      <td className={cn('px-6 py-4 whitespace-nowrap text-sm font-bold', colors.text)}>
                        {MONTH_NAMES[row.month - 1]}
                      </td>
                      <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                        {formatCurrency(row.actualExpenses)}
                      </td>
                      <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                        {formatCurrency(row.pendingPayables)}
                      </td>
                      <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                        {formatCurrency(row.paidPayables)}
                      </td>
                      <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                        {formatCurrency(row.recurringProjected)}
                      </td>
                      <td className={cn('px-6 py-4 whitespace-nowrap text-sm text-right font-semibold sticky right-0 z-10', colors.text, colors.surface)}>
                        {formatCurrency(row.totalProjected)}
                      </td>
                    </tr>
                  ))}
                  <tr className={cn('font-bold border-t-2', colors.border, colors.surfaceHover)}>
                    <td className={cn('px-6 py-4 whitespace-nowrap text-sm', colors.text)}>
                      Total
                    </td>
                    <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                      {formatCurrency(totalActual)}
                    </td>
                    <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                      {formatCurrency(totalPending)}
                    </td>
                    <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                      {formatCurrency(totalPaid)}
                    </td>
                    <td className={cn('px-4 py-4 whitespace-nowrap text-sm text-right', colors.text)}>
                      {formatCurrency(totalRecurring)}
                    </td>
                    <td className={cn('px-6 py-4 whitespace-nowrap text-sm text-right sticky right-0 z-10', colors.text, colors.surface)}>
                      {formatCurrency(totalProjected)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={cn('rounded-xl border p-12 text-center', colors.surface, colors.border)}>
            <svg className={cn('w-16 h-16 mx-auto mb-4', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className={cn('text-lg font-medium', colors.textMuted)}>No data available for the selected year.</p>
            <p className={cn('text-sm mt-2', colors.textMuted)}>Try selecting a different year.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <Link to="/payables" className={cn('text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline')}>
                View payables
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default List;
